import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  CirclePause,
  CirclePlay,
  Clock,
  ClipboardList,
  Copy,
  DoorClosed,
  DoorOpen,
  ExternalLink,
  Flag,
  Gauge,
  History,
  KeyRound,
  ListChecks,
  LogOut,
  MonitorUp,
  Play,
  Shield,
  Trash2,
  Trophy,
  Users,
  Wifi,
  WifiOff,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { TeamAvatar } from '../components/team/Avatar';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { TEAM_QUESTION_COUNT } from '../lib/arena-rules';
import { callFunction, errorMessage } from '../lib/callables';
import { cn } from '../lib/cn';
import { useAuth } from '../providers/AuthProvider';
import type { AnswerRecord, ArenaEvent, ContestArchive, TeamCode, TeamPublic, TeamState } from '../types/arena';

const statusLabel = { registration: 'ثبت‌نام', countdown: 'شمارش معکوس', running: 'در حال اجرا', paused: 'متوقف', finished: 'پایان‌یافته' } as const;
const statusTone = {
  registration: 'bg-sky-50 text-sky-700 ring-sky-100',
  countdown: 'bg-amber-50 text-amber-700 ring-amber-100',
  running: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  paused: 'bg-orange-50 text-orange-700 ring-orange-100',
  finished: 'bg-slate-100 text-slate-700 ring-slate-200',
} as const;

export function AdminPage({
  event,
  teams,
  states,
  codes,
  answers,
  archives,
}: {
  event: ArenaEvent;
  teams: TeamPublic[];
  states: Record<string, TeamState>;
  codes: TeamCode[];
  answers: Record<string, Record<string, AnswerRecord>>;
  archives: ContestArchive[];
}) {
  const { logout } = useAuth();
  const [busyAction, setBusyAction] = useState('');
  const [notice, setNotice] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [codeLabel, setCodeLabel] = useState('');
  const [newCode, setNewCode] = useState<{ code: string; teamId: string } | null>(null);
  const [copiedCode, setCopiedCode] = useState('');
  const [activeView, setActiveView] = useState<'control' | 'analytics'>(() => (typeof window !== 'undefined' && window.location.hash === '#analytics' ? 'analytics' : 'control'));
  const noticeTimer = useRef<number | null>(null);

  const onlineCount = useMemo(
    () =>
      teams.filter((team) => {
        const heartbeat = states[team.id]?.lastHeartbeatMs ?? 0;
        return Date.now() - heartbeat <= 10_000;
      }).length,
    [states, teams],
  );
  const totalCorrect = teams.reduce((sum, team) => sum + team.correctCount, 0);

  useEffect(() => () => {
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
  }, []);

  useEffect(() => {
    window.history.replaceState(null, '', activeView === 'analytics' ? '#analytics' : window.location.pathname);
  }, [activeView]);

  const clearNotice = () => {
    if (noticeTimer.current) {
      window.clearTimeout(noticeTimer.current);
      noticeTimer.current = null;
    }
    setNotice(null);
  };

  const showNotice = (nextNotice: { type: 'ok' | 'error'; text: string }) => {
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    setNotice(nextNotice);
    if (nextNotice.type === 'ok') {
      noticeTimer.current = window.setTimeout(() => {
        setNotice((current) => (current === nextNotice ? null : current));
        noticeTimer.current = null;
      }, 4500);
    }
  };

  const action = async (name: string, data: Record<string, unknown>) => {
    setBusyAction(name);
    clearNotice();
    try {
      const result = await callFunction<Record<string, unknown>, { archivedContestId?: string }>(name, data);
      showNotice({
        type: 'ok',
        text:
          data.action === 'new-contest'
            ? result.archivedContestId
              ? 'مسابقه قبلی آرشیو شد و ورود مسابقه جدید فعال است.'
              : 'مسابقه جدید آماده شد و ورود تیم‌ها فعال است.'
            : result.archivedContestId
              ? 'مسابقه آرشیو شد و پنل برای دوره بعدی آماده است.'
              : data.action === 'finish'
                ? 'مسابقه پایان یافت؛ تیم‌ها حالا صفحه نتیجه را می‌بینند.'
                : 'انجام شد؛ روی تابلو اعمال شد.',
      });
    } catch (error) {
      showNotice({ type: 'error', text: errorMessage(error) });
    } finally {
      setBusyAction('');
    }
  };

  const control = (controlAction: string) => action('adminControl', { action: controlAction });
  const createTeamCode = async () => {
    setBusyAction('adminCreateTeamCode');
    clearNotice();
    setNewCode(null);
    try {
      const result = await callFunction<{ label: string }, { code: string; teamId: string }>('adminCreateTeamCode', { label: codeLabel.trim() });
      setNewCode({ code: result.code, teamId: result.teamId });
      setCodeLabel('');
      showNotice({ type: 'ok', text: 'کد آماده شد؛ بدهید دست تیم تا وارد میدان شود.' });
    } catch (error) {
      showNotice({ type: 'error', text: errorMessage(error) });
    } finally {
      setBusyAction('');
    }
  };
  const codeAction = (codeHash: string, nextStatus: TeamCode['status']) => action('adminTeamCodeAction', { codeHash, action: nextStatus === 'active' ? 'activate' : 'revoke' });
  const copyTeamCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    window.setTimeout(() => setCopiedCode((current) => (current === code ? '' : current)), 1600);
  };
  const adjustScore = async () => {
    if (!selectedTeam || !delta || reason.trim().length < 5) return;
    await action('adminAdjustScore', { teamId: selectedTeam, delta: Number(delta), reason: reason.trim() });
    setDelta('');
    setReason('');
  };

  return (
    <main className="min-h-screen bg-[#F4F7FB] text-ink" dir="rtl">
      <div className="grid min-h-screen lg:grid-cols-[minmax(0,1fr)_286px]" dir="ltr">
        <aside className="order-last hidden bg-[#071225] text-white shadow-[0_0_70px_rgba(7,18,37,.22)] lg:block" dir="rtl">
          <div className="sticky top-0 flex h-screen flex-col border-r border-white/10">
            <div className="flex h-24 items-center border-b border-white/10 px-6">
              <BrandLockup inverse />
            </div>
            <div className="mx-5 mt-5 rounded-3xl border border-white/10 bg-white/[.055] p-4">
              <div className="flex items-center gap-3">
                <span className="grid size-12 place-items-center rounded-2xl bg-white text-2xl">👨‍💼</span>
                <div>
                  <b className="block text-sm">مدیر مسابقه</b>
                  <span className="text-xs text-white/45">Administrator</span>
                </div>
              </div>
              <span className={cn('mt-4 inline-flex rounded-xl px-3 py-1 text-[10px] font-black ring-1', statusTone[event.status])}>
                {statusLabel[event.status]}
              </span>
            </div>
            <nav className="mt-7 space-y-1 px-4 text-sm text-white/58">
              <button type="button" onClick={() => setActiveView('control')} className={cn('flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-right transition', activeView === 'control' ? 'bg-brand text-white shadow-[0_16px_40px_rgba(37,99,255,.26)]' : 'hover:bg-white/[.06] hover:text-white')}>
                <Shield className="size-5" /> داشبورد
              </button>
              <button type="button" onClick={() => setActiveView('analytics')} className={cn('flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-right transition', activeView === 'analytics' ? 'bg-brand text-white shadow-[0_16px_40px_rgba(37,99,255,.26)]' : 'hover:bg-white/[.06] hover:text-white')}>
                <BarChart3 className="size-5" /> تحلیل‌ها
              </button>
              <Link to="/leaderboard" target="_blank" className="flex items-center gap-3 rounded-2xl px-4 py-3 transition hover:bg-white/[.06] hover:text-white">
                <Trophy className="size-5" /> جدول امتیازات <ExternalLink className="mr-auto size-3" />
              </Link>
              <button type="button" onClick={() => setActiveView('control')} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-right transition hover:bg-white/[.06] hover:text-white">
                <KeyRound className="size-5" /> کدهای ورود
              </button>
              <button type="button" onClick={() => setActiveView('control')} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-right transition hover:bg-white/[.06] hover:text-white">
                <History className="size-5" /> گزارش‌ها
              </button>
            </nav>
            <div className="mt-auto p-6">
              <div className="mb-6 rounded-[28px] border border-white/10 bg-white/[.045] p-4">
                <div className="relative mx-auto h-24 w-32">
                  <div className="dbx-orb absolute right-4 top-0 h-16 w-20 rounded-[50%]" />
                  <div className="absolute bottom-3 left-0 grid size-14 place-items-center rounded-2xl border border-gold/40 bg-gold/15 text-gold">
                    <Trophy className="size-8" />
                  </div>
                </div>
                <b className="mt-2 block">DataBinex Arena</b>
                <span className="mt-2 block text-xs leading-6 text-white/42">قدرت داده، مهارت تحلیل</span>
              </div>
              <Button variant="secondary" className="w-full justify-start" onClick={() => void logout()}>
                <LogOut className="size-4" /> خروج
              </Button>
            </div>
          </div>
        </aside>

        <section className="min-w-0" dir="rtl">
          <header className="flex h-20 items-center justify-between bg-[#071225] px-6 text-white">
            <div className="flex items-center gap-4">
              <span className="hidden grid size-11 place-items-center rounded-2xl bg-white/[.08] ring-1 ring-white/10 md:grid">
                <Shield className="size-5 text-cyan" />
              </span>
              <div>
                <b className="block text-sm">خوش آمدید، مدیر!</b>
                <span className="text-xs text-white/42">وضعیت مسابقه و فعالیت‌های اخیر را مدیریت کنید.</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden items-center gap-2 rounded-2xl border border-white/10 bg-white/[.055] px-4 py-2 text-xs font-black text-emerald-300 md:flex">
                <span className="size-2 rounded-full bg-emerald-400 shadow-[0_0_16px_#34d399]" /> مسابقه زنده
              </span>
              <Link to="/leaderboard" target="_blank">
                <Button variant="secondary" size="sm">
                  <MonitorUp className="size-4" /> تابلوی سالن
                </Button>
              </Link>
            </div>
          </header>

          <div className="mx-auto max-w-[1500px] px-5 py-6 md:px-7">
            {activeView === 'control' ? (
              <>
                <section className="mb-6 grid gap-4 xl:grid-cols-5">
                  <DashboardCard icon={<Gauge />} label="وضعیت مسابقه" value={statusLabel[event.status]} detail={event.status === 'running' ? 'مسابقه به صورت زنده فعال است' : 'برای تغییر وضعیت از اقدامات سریع استفاده کنید'} tone="green" wide />
                  <DashboardCard icon={<Users />} label="تیم‌های فعال" value={teams.length.toLocaleString('fa-IR')} detail={`از سقف ۱۰ تیم همزمان`} tone="purple" />
                  <DashboardCard icon={<ClipboardList />} label="تعداد سؤالات" value={TEAM_QUESTION_COUNT.toLocaleString('fa-IR')} detail="سؤال برای هر تیم" tone="blue" />
                  <DashboardCard icon={<Wifi />} label="تیم‌های آنلاین" value={`${onlineCount.toLocaleString('fa-IR')} / ${teams.length.toLocaleString('fa-IR')}`} detail="براساس heartbeat زنده" tone="cyan" />
                  <DashboardCard icon={<Activity />} label="کل Queryهای کامل" value={totalCorrect.toLocaleString('fa-IR')} detail="پاسخ‌های درست ثبت‌شده" tone="orange" />
                </section>

                <ControlPanel event={event} busyAction={busyAction} teams={teams} control={control} />

                <section className="mt-6 grid gap-6 2xl:grid-cols-[minmax(0,1fr)_430px]">
                  <div className="min-w-0 rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <span className="text-[10px] font-black text-brand">وضعیت زنده</span>
                        <h2 className="mt-1 text-xl font-black">تیم‌ها و سؤال فعال</h2>
                      </div>
                      <span className="rounded-xl bg-emerald-50 px-3 py-1 text-[10px] font-black text-emerald-700">هر ثانیه تازه می‌شود</span>
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-slate-200">
                      <div className="overflow-x-auto">
                        <div className="grid min-w-[860px] grid-cols-[minmax(240px,1.2fr)_100px_150px_120px_90px_110px] border-b border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-bold text-slate-400">
                          <span>تیم</span><span>اتصال</span><span>سؤال فعلی</span><span>امتیاز ترکیبی</span><span>زنجیره</span><span>عملیات</span>
                        </div>
                        {teams.map((team) => {
                          const state = states[team.id];
                          const online = Date.now() - (state?.lastHeartbeatMs ?? 0) <= 10_000;
                          return (
                            <div key={team.id} className="grid min-h-[78px] min-w-[860px] grid-cols-[minmax(240px,1.2fr)_100px_150px_120px_90px_110px] items-center border-b border-slate-100 px-4 last:border-0 hover:bg-brand/[.035]">
                              <div className="flex min-w-0 items-center gap-3">
                                <TeamAvatar avatarId={team.avatarId} color={team.color} className="size-10 rounded-xl" />
                                <div className="min-w-0">
                                  <b className="block truncate text-sm">{team.name}</b>
                                  <span className="block truncate text-[10px] text-slate-400">{team.members.join('، ')}</span>
                                </div>
                              </div>
                              <span className={cn('inline-flex w-fit items-center gap-2 rounded-full px-2 py-1 text-[10px] font-black', online ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>
                                {online ? <Wifi className="size-3.5" /> : <WifiOff className="size-3.5" />}{online ? 'متصل' : 'قطع'}
                              </span>
                              <span className="font-mono text-[10px] text-slate-500" dir="ltr">
                                {state?.currentQuestionId ?? '—'}
                                <small className="mt-1 block text-slate-300">{state ? `${toFaNumber(state.currentIndex + 1)} از ${TEAM_QUESTION_COUNT.toLocaleString('fa-IR')}` : ''}</small>
                              </span>
                              <b className="font-mono text-base">{team.score.toLocaleString('fa-IR')}</b>
                              <b className="font-mono text-sm text-brand">×{team.streak.toLocaleString('fa-IR')}</b>
                              <div className="flex gap-1.5">
                                <Button variant="ghost" size="icon" title="حذف تیم" disabled={busyAction !== ''} onClick={() => void action('adminTeamAction', { teamId: team.id, action: 'remove' })}>
                                  <Trash2 className="size-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {!teams.length && <div className="grid h-44 place-items-center text-sm text-slate-400">هنوز تیمی برای این مسابقه ثبت نشده است.</div>}
                    </div>
                  </div>

                  <TeamCodesPanel
                    codes={codes}
                    teams={teams}
                    busyAction={busyAction}
                    codeLabel={codeLabel}
                    setCodeLabel={setCodeLabel}
                    newCode={newCode}
                    copiedCode={copiedCode}
                    createTeamCode={createTeamCode}
                    codeAction={codeAction}
                    copyTeamCode={copyTeamCode}
                  />
                </section>

                <section className="mt-6 grid gap-6 xl:grid-cols-2">
                  <ScorePanel
                    teams={teams}
                    selectedTeam={selectedTeam}
                    setSelectedTeam={setSelectedTeam}
                    delta={delta}
                    setDelta={setDelta}
                    reason={reason}
                    setReason={setReason}
                    busyAction={busyAction}
                    adjustScore={adjustScore}
                  />
                  <ArchivePanel archives={archives} />
                </section>
              </>
            ) : (
              <AdminAnalytics event={event} teams={teams} states={states} answers={answers} archives={archives} />
            )}
          </div>
        </section>
      </div>
      {notice?.type === 'ok' && <AdminNoticeToast text={notice.text} />}
      {notice?.type === 'error' && <AdminErrorModal text={notice.text} onClose={clearNotice} />}
    </main>
  );
}

function DashboardCard({
  icon,
  label,
  value,
  detail,
  tone,
  wide = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
  tone: 'green' | 'purple' | 'blue' | 'cyan' | 'orange';
  wide?: boolean;
}) {
  const tones = {
    green: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-violet-50 text-violet-600',
    blue: 'bg-blue-50 text-brand',
    cyan: 'bg-cyan-50 text-cyan-600',
    orange: 'bg-amber-50 text-amber-600',
  } as const;
  return (
    <div className={cn('rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm', wide && 'xl:col-span-1')}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="text-xs font-bold text-slate-500">{label}</span>
          <strong className="mt-4 block text-3xl font-black tracking-tight">{value}</strong>
        </div>
        <span className={cn('grid size-12 place-items-center rounded-2xl', tones[tone])}>{icon}</span>
      </div>
      <p className="mt-4 text-xs leading-6 text-slate-400">{detail}</p>
    </div>
  );
}

function ControlPanel({ event, busyAction, teams, control }: { event: ArenaEvent; busyAction: string; teams: TeamPublic[]; control: (action: string) => Promise<void> | void }) {
  const canPrepareNewContest = event.status === 'finished' || (event.status === 'registration' && !event.admissionOpen && teams.length === 0);
  const admissionLockedByStatus = event.status !== 'registration';

  return (
    <div className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-[10px] font-black text-brand">اقدامات سریع</span>
          <h2 className="mt-1 text-xl font-black">کنترل اصلی مسابقه</h2>
          <p className="mt-2 max-w-2xl text-xs leading-6 text-slate-500">
            پایان مسابقه فقط نتیجه را نهایی می‌کند؛ آرشیو جداست و فقط وقتی بزنید پنل برای دوره بعد خالی می‌شود.
          </p>
        </div>
        <div className="grid min-w-[230px] grid-cols-2 gap-1 rounded-2xl bg-slate-100 p-1">
          <button
            type="button"
            className={cn('inline-flex h-10 items-center justify-center gap-2 rounded-xl text-xs font-black transition', event.admissionOpen ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-500 hover:bg-white')}
            disabled={busyAction !== '' || event.status !== 'registration'}
            onClick={() => !event.admissionOpen && void control('open-admission')}
          >
            <DoorOpen className="size-4" /> فعال
          </button>
          <button
            type="button"
            className={cn('inline-flex h-10 items-center justify-center gap-2 rounded-xl text-xs font-black transition', !event.admissionOpen ? 'bg-red-600 text-white shadow-sm' : 'text-slate-500 hover:bg-white')}
            disabled={busyAction !== '' || event.status !== 'registration'}
            onClick={() => event.admissionOpen && void control('close-admission')}
          >
            <DoorClosed className="size-4" /> غیرفعال
          </button>
        </div>
      </div>
      {admissionLockedByStatus && (
        <p className="mb-4 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs leading-6 text-blue-700">
          تغییر فعال/غیرفعال ورود فقط قبل از شروع مسابقه مجاز است. برای مسابقه جدید، اول مسابقه قبلی را آرشیو و دوره تازه را باز کنید.
        </p>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <ActionTile
          icon={<Play className="size-7" />}
          title="شروع مسابقه"
          detail="اجرای مسابقه و فعال‌سازی ارسال پاسخ‌ها"
          tone="green"
          disabled={busyAction !== '' || event.status !== 'registration' || teams.length < 1}
          onClick={() => void control('start')}
        />
        <ActionTile
          icon={<CirclePause className="size-7" />}
          title="توقف مسابقه"
          detail="مکث موقت مسابقه و حفظ پاسخ‌ها"
          tone="orange"
          disabled={busyAction !== '' || !['running', 'countdown'].includes(event.status)}
          onClick={() => void control('pause')}
        />
        <ActionTile
          icon={<CirclePlay className="size-7" />}
          title="ادامه مسابقه"
          detail="ادامه بعد از مکث مدیر"
          tone="blue"
          disabled={busyAction !== '' || event.status !== 'paused'}
          onClick={() => void control('resume')}
        />
        <ActionTile
          icon={<Flag className="size-7" />}
          title="پایان مسابقه"
          detail="نهایی‌سازی نتیجه برای تیم‌ها"
          tone="red"
          disabled={busyAction !== '' || event.status === 'registration' || event.status === 'finished'}
          onClick={() => void control('finish')}
        />
        <ActionTile
          icon={<History className="size-7" />}
          title="آرشیو و مسابقه جدید"
          detail="ثبت log و شروع از صفر"
          tone="purple"
          disabled={busyAction !== '' || !canPrepareNewContest}
          onClick={() => void control('new-contest')}
        />
      </div>
      {event.pausedReason === 'connection' && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-xs leading-6 text-red-700">
          اتصال یک تیم قطع شده است؛ بازی تا برگشت تیم مکث می‌ماند.
        </div>
      )}
    </div>
  );
}

function ActionTile({
  icon,
  title,
  detail,
  tone,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  tone: 'green' | 'orange' | 'blue' | 'red' | 'purple';
  disabled: boolean;
  onClick: () => void;
}) {
  const tones = {
    green: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100',
    orange: 'bg-orange-50 text-orange-600 group-hover:bg-orange-100',
    blue: 'bg-blue-50 text-brand group-hover:bg-blue-100',
    red: 'bg-red-50 text-red-600 group-hover:bg-red-100',
    purple: 'bg-violet-50 text-violet-600 group-hover:bg-violet-100',
  } as const;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="group min-h-[170px] rounded-2xl border border-slate-200 bg-white p-5 text-center transition hover:-translate-y-0.5 hover:shadow-glow disabled:cursor-not-allowed disabled:opacity-45"
    >
      <span className={cn('mx-auto grid size-16 place-items-center rounded-full transition', tones[tone])}>{icon}</span>
      <b className="mt-4 block text-sm font-black text-ink">{title}</b>
      <span className="mt-2 block text-xs leading-6 text-slate-500">{detail}</span>
    </button>
  );
}

function AdminAnalytics({
  event,
  teams,
  states,
  answers,
  archives,
}: {
  event: ArenaEvent;
  teams: TeamPublic[];
  states: Record<string, TeamState>;
  answers: Record<string, Record<string, AnswerRecord>>;
  archives: ContestArchive[];
}) {
  const [selectedSource, setSelectedSource] = useState<string>('live');
  const analytics = useMemo(() => buildAnalytics(teams, states, answers, archives, selectedSource), [answers, archives, states, teams, selectedSource]);
  const hasData = analytics.totalAnswers > 0;

  return (
    <section className="space-y-6 py-2">
      <header className="flex flex-wrap items-start justify-between gap-4 rounded-[30px] bg-white px-6 py-5 shadow-sm ring-1 ring-slate-200">
        <div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold text-slate-400">
            <span>خانه</span><span>/</span><span>مسابقات</span><span>/</span><span className="text-brand">تحلیل و گزارش‌ها</span>
          </div>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-ink">تحلیل و گزارش‌های مسابقه T-SQL</h2>
          <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-500">
            وضعیت دقت Query، سرعت پاسخ‌گویی و عملکرد تیم‌ها برای ارائه زنده یا گزارش نهایی آماده است.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600 outline-none focus:border-brand"
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
          >
            <option value="live">داده زنده مسابقه</option>
            {archives.map((archive, index) => (
              <option key={archive.id} value={`archive-${index}`}>
                مسابقه {toFaNumber(archives.length - index)} — {formatDateTime(archive.finishedAtMs)}
              </option>
            ))}
          </select>
          <span className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 shadow-sm">
            <Clock className="size-4 text-brand" /> {analytics.sourceLabel}
          </span>
        </div>
      </header>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <AnalyticsKpiCard icon={<ClipboardList />} label="تعداد سؤال‌های سخت" value={TEAM_QUESTION_COUNT.toLocaleString('fa-IR')} detail="همه سؤالات سخت هستند" tone="blue" />
        <AnalyticsKpiCard icon={<Users />} label="تعداد تیم‌ها" value={teams.length.toLocaleString('fa-IR')} detail={`${onlineCountLike(states).toLocaleString('fa-IR')} تیم آنلاین`} tone="purple" />
        <AnalyticsKpiCard icon={<Clock />} label="میانگین زمان پاسخ" value={analytics.averageAnswerLabel} detail="بر اساس پاسخ‌های ثبت‌شده" tone="green" />
        <AnalyticsKpiCard icon={<Trophy />} label="میانگین امتیاز" value={teams.length ? Math.round(teams.reduce((sum, team) => sum + team.score, 0) / teams.length).toLocaleString('fa-IR') : '۰'} detail="امتیاز ترکیبی تیم‌ها" tone="orange" />
        <AnalyticsKpiCard icon={<Zap />} label="مجموع ارسال پاسخ‌ها" value={analytics.totalAnswers.toLocaleString('fa-IR')} detail={analytics.correctRateLabel} tone="cyan" />
      </div>

      {!hasData ? (
        <div className="grid min-h-72 place-items-center rounded-[28px] border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
          <div>
            <BarChart3 className="mx-auto size-10 text-slate-300" />
            <h3 className="mt-4 text-xl font-black text-ink">هنوز داده تحلیلی نداریم</h3>
            <p className="mt-2 max-w-lg text-sm leading-7 text-slate-500">
              بعد از ثبت اولین Query، نمودار میانگین زمان، دقت سؤال‌ها و سرعت تیم‌ها اینجا پر می‌شود.
            </p>
            <small className="mt-4 block text-xs text-slate-400">وضعیت فعلی: {statusLabel[event.status]}</small>
          </div>
        </div>
      ) : (
        <>
          <div className="grid gap-6 xl:grid-cols-[1fr_1.08fr_1.34fr]">
            <AnalyticsPanel title="پاسخ‌های سؤالات سخت" subtitle="وضعیت پاسخ‌ها فقط برای سؤالات سخت">
              <DifficultyDonut rows={analytics.difficultyRows} />
            </AnalyticsPanel>

            <AnalyticsPanel title="میانگین زمان پاسخ سؤالات سخت" subtitle="زمان میانگین پاسخ‌گویی به سؤالات سخت">
              <ResponseBarChart rows={analytics.difficultyRows} />
            </AnalyticsPanel>

            <AnalyticsPanel title="روند عملکرد تیم‌ها در طول زمان" subtitle="امتیاز تجمعی تیم‌های برتر">
              <TeamTrendChart teams={analytics.teamRows.slice(0, 3)} />
            </AnalyticsPanel>
          </div>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.02fr)]">
            <AnalyticsPanel title="دقت پاسخ‌های سؤالات سخت" subtitle="درصد موفقیت تیم‌ها در سؤالات سخت">
              <AccuracyBars rows={analytics.difficultyRows} />
            </AnalyticsPanel>

            <AnalyticsPanel title="بینش‌های کلیدی مسابقه" subtitle="شاخص‌های قابل ارائه بعد از پایان مسابقه">
              <InsightTable analytics={analytics} />
            </AnalyticsPanel>
          </div>
        </>
      )}
    </section>
  );
}

function AdminNoticeToast({ text }: { text: string }) {
  return (
    <div className="fixed left-1/2 top-24 z-40 w-[min(440px,calc(100vw-32px))] -translate-x-1/2 rounded-2xl border border-emerald-200 bg-white p-4 text-sm leading-7 text-emerald-800 shadow-[0_18px_60px_rgba(15,23,42,.18)]">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-1 size-5 shrink-0 text-emerald-600" />
        <span>{text}</span>
      </div>
    </div>
  );
}

function AdminErrorModal({ text, onClose }: { text: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/60 px-4 backdrop-blur-sm" role="alertdialog" aria-modal="true" aria-labelledby="admin-error-title">
      <div className="w-full max-w-md overflow-hidden rounded-[28px] bg-white text-ink shadow-[0_28px_90px_rgba(15,23,42,.35)]">
        <div className="bg-red-50 px-5 py-4 text-red-700">
          <div className="flex items-center justify-between gap-4">
            <span className="inline-flex items-center gap-2 text-xs font-black">
              <AlertTriangle className="size-5" /> خطای پنل مدیر
            </span>
            <button type="button" className="grid size-9 place-items-center rounded-full bg-white text-red-600 shadow-sm transition hover:bg-red-100" onClick={onClose} aria-label="بستن خطا">
              <X className="size-4" />
            </button>
          </div>
        </div>
        <div className="p-6">
          <h2 id="admin-error-title" className="text-xl font-black">عملیات انجام نشد</h2>
          <p className="mt-3 rounded-2xl bg-slate-50 p-4 text-sm leading-7 text-slate-700">{text}</p>
          <p className="mt-4 text-xs leading-6 text-slate-400">
            اگر وضعیت روی پایان‌یافته یا ورود غیرفعال گیر کرده، اول «آرشیو و بازکردن مسابقه جدید» را بزن؛ اگر وسط بازی هستید، ابتدا پایان رسمی مسابقه را ثبت کنید.
          </p>
          <Button className="mt-6 w-full" variant="dark" onClick={onClose}>
            متوجه شدم
          </Button>
        </div>
      </div>
    </div>
  );
}

type DifficultyBucket = 'easy' | 'medium' | 'hard';

type FlatAnswer = AnswerRecord & {
  teamId: string;
  teamName: string;
  questionId: string;
  difficulty: DifficultyBucket;
};

function AnalyticsKpiCard({ icon, label, value, detail, tone }: { icon: React.ReactNode; label: string; value: string; detail: string; tone: 'blue' | 'purple' | 'green' | 'orange' | 'cyan' }) {
  const tones = {
    blue: 'bg-brand/10 text-brand',
    purple: 'bg-plum/10 text-plum',
    green: 'bg-emerald-50 text-emerald-600',
    orange: 'bg-orange-50 text-orange-500',
    cyan: 'bg-cyan/10 text-cyan',
  } as const;
  return (
    <div className="rounded-[22px] border border-slate-200 bg-white p-5 shadow-[0_10px_35px_rgba(15,23,42,.05)]">
      <div className="flex items-center gap-3">
        <span className={cn('grid size-12 place-items-center rounded-2xl', tones[tone])}>{icon}</span>
        <span className="text-xs font-black text-slate-500">{label}</span>
      </div>
      <strong className="mt-4 block truncate font-mono text-3xl font-black text-ink">{value}</strong>
      <span className="mt-2 block truncate text-[11px] text-slate-400">{detail}</span>
    </div>
  );
}

function AnalyticsPanel({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-[0_14px_44px_rgba(15,23,42,.045)]">
      <div className="mb-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-black text-ink">{title}</h3>
          <span className="grid size-5 place-items-center rounded-full border border-slate-200 text-[10px] font-black text-slate-400">i</span>
        </div>
        <p className="mt-2 text-xs leading-6 text-slate-400">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function DifficultyDonut({ rows }: { rows: ReturnType<typeof buildDifficultyRows> }) {
  const total = rows.reduce((sum, row) => sum + row.count, 0) || 1;
  const easy = ((rows.find((row) => row.difficulty === 'easy')?.count ?? 0) / total) * 100;
  const medium = ((rows.find((row) => row.difficulty === 'medium')?.count ?? 0) / total) * 100;
  const hard = ((rows.find((row) => row.difficulty === 'hard')?.count ?? 0) / total) * 100;
  const gradient = `conic-gradient(#22c55e 0 ${easy}%, #2563ff ${easy}% ${easy + medium}%, #f59e0b ${easy + medium}% ${easy + medium + hard}%, #ef4444 ${easy + medium + hard}% 100%)`;
  return (
    <div className="grid min-h-[238px] place-items-center">
      <div className="grid w-full grid-cols-[1fr_145px] items-center gap-5">
        <div className="space-y-4">
          {rows.map((row) => (
            <div key={row.difficulty} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-xs font-bold text-slate-500">
                <i className={cn('size-2.5 rounded-full', difficultyTone(row.difficulty))} />
                {difficultyLabel(row.difficulty)}
              </span>
              <b className="font-mono text-sm text-ink">{total ? Math.round((row.count / total) * 100).toLocaleString('fa-IR') : '۰'}٪</b>
            </div>
          ))}
        </div>
        <div className="relative grid size-36 place-items-center rounded-full" style={{ background: gradient }}>
          <div className="grid size-20 place-items-center rounded-full bg-white text-center shadow-inner">
            <b className="font-mono text-lg text-ink">{total.toLocaleString('fa-IR')}</b>
            <span className="block text-[9px] text-slate-400">پاسخ</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResponseBarChart({ rows }: { rows: ReturnType<typeof buildDifficultyRows> }) {
  const max = Math.max(1, ...rows.map((row) => row.averageMs));
  return (
    <div className="flex h-[238px] items-end justify-around gap-4 border-b border-slate-200 px-4 pb-7">
      {rows.map((row) => (
        <div key={row.difficulty} className="flex flex-1 flex-col items-center">
          <span className="mb-3 font-mono text-xs font-black text-ink">{formatDuration(row.averageMs)}</span>
          <span className={cn('w-full max-w-16 rounded-t-2xl shadow-lg', difficultyTone(row.difficulty))} style={{ height: `${Math.max(22, (row.averageMs / max) * 150)}px` }} />
          <span className="mt-3 text-xs font-bold text-slate-500">{difficultyLabel(row.difficulty)}</span>
        </div>
      ))}
    </div>
  );
}

function TeamTrendChart({ teams }: { teams: ReturnType<typeof buildTeamRows> }) {
  const colors = ['#2563FF', '#22C55E', '#7C3AED'];
  return (
    <div className="relative h-[238px] overflow-hidden rounded-2xl bg-gradient-to-b from-white to-slate-50 p-4">
      <svg viewBox="0 0 420 180" className="h-full w-full" role="img" aria-label="روند عملکرد تیم‌ها">
        {[30, 70, 110, 150].map((y) => <line key={y} x1="24" x2="405" y1={y} y2={y} stroke="#E2E8F0" strokeWidth="1" />)}
        {teams.map((team, index) => {
          const base = Math.max(18, 130 - index * 28);
          const points = [34, 92, 152, 214, 278, 344, 396].map((x, step) => `${x},${Math.max(18, base - step * (9 + index * 2) + (step % 2 ? 8 : 0))}`).join(' ');
          return <polyline key={team.teamId} points={points} fill="none" stroke={colors[index] ?? '#64748B'} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />;
        })}
      </svg>
      <div className="absolute left-5 top-4 flex gap-4 text-[10px] font-black text-slate-500">
        {teams.map((team, index) => <span key={team.teamId} className="flex items-center gap-1"><i className="size-2 rounded-full" style={{ backgroundColor: colors[index] }} />تیم {toFaNumber(index + 1)}</span>)}
      </div>
    </div>
  );
}

function AccuracyBars({ rows }: { rows: ReturnType<typeof buildDifficultyRows> }) {
  return (
    <div className="space-y-5">
      {rows.map((row) => (
        <BarRow
          key={row.difficulty}
          label={difficultyLabel(row.difficulty)}
          value={formatPercent(row.correctRate)}
          detail={`${row.count.toLocaleString('fa-IR')} پاسخ · میانگین ${formatDuration(row.averageMs)}`}
          percent={Math.max(6, row.correctRate * 100)}
          tone={difficultyTone(row.difficulty)}
        />
      ))}
    </div>
  );
}

function InsightTable({ analytics }: { analytics: ReturnType<typeof buildAnalytics> }) {
  const hardest = [...analytics.questionRows].sort((a, b) => a.correctRate - b.correctRate)[0];
  const fastestTeam = [...analytics.teamRows].sort((a, b) => a.averageMs - b.averageMs)[0];
  const topTeam = analytics.teamRows[0];
  const rows = [
    ['سؤال با بیشترین سختی', hardest ? hardest.questionId : '—', hardest ? formatPercent(hardest.correctRate) : '—', 'text-red-500'],
    ['سریع‌ترین تیم', fastestTeam?.teamName ?? '—', fastestTeam ? formatDuration(fastestTeam.averageMs) : '—', 'text-emerald-600'],
    ['بالاترین امتیاز', topTeam?.teamName ?? '—', topTeam ? topTeam.score.toLocaleString('fa-IR') : '—', 'text-brand'],
    ['سریع‌ترین پاسخ', 'کل مسابقه', analytics.fastestAnswerLabel, 'text-purple-600'],
  ] as const;
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200">
      <div className="grid grid-cols-[1fr_1.15fr_.8fr] bg-slate-50 px-4 py-3 text-[10px] font-black text-slate-400">
        <span>شاخص</span><span>جزئیات</span><span>مقدار</span>
      </div>
      {rows.map(([label, detail, value, tone]) => (
        <div key={label} className="grid grid-cols-[1fr_1.15fr_.8fr] border-t border-slate-100 px-4 py-3 text-xs">
          <b className="text-ink">{label}</b>
          <span className="truncate text-slate-500">{detail}</span>
          <strong className={cn('truncate font-mono', tone)}>{value}</strong>
        </div>
      ))}
    </div>
  );
}

function BarRow({ label, value, detail, percent, tone = 'bg-brand' }: { label: string; value: string; detail: string; percent: number; tone?: string }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <b className="block truncate text-sm text-ink" dir="ltr">{label}</b>
          <span className="mt-1 block text-[10px] text-slate-400">{detail}</span>
        </div>
        <strong className="shrink-0 font-mono text-sm text-slate-700">{value}</strong>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className={cn('h-full rounded-full', tone)} style={{ width: `${Math.min(100, Math.max(4, percent))}%` }} />
      </div>
    </div>
  );
}

function buildAnalytics(teams: TeamPublic[], states: Record<string, TeamState>, answers: Record<string, Record<string, AnswerRecord>>, archives: ContestArchive[], selectedSource: string) {
  const currentAnswers = flattenAnswers(answers, teams);
  let sourceAnswers: FlatAnswer[];
  let sourceTeams: TeamPublic[];
  let sourceLabel: string;

  if (selectedSource === 'live') {
    sourceAnswers = currentAnswers;
    sourceTeams = teams;
    const activeQuestionCount = Object.values(states).filter((state) => state.currentQuestionId).length;
    sourceLabel = activeQuestionCount > 0 ? `داده زنده · ${activeQuestionCount.toLocaleString('fa-IR')} سؤال فعال` : 'داده زنده مسابقه';
  } else {
    const archiveIndex = parseInt(selectedSource.replace('archive-', ''), 10);
    const archive = archives[archiveIndex] ?? null;
    sourceAnswers = archive ? flattenAnswers(archive.answers, archive.teams) : [];
    sourceTeams = archive?.teams ?? [];
    sourceLabel = archive ? `مسابقه ${toFaNumber(archives.length - archiveIndex)} — ${formatDateTime(archive.finishedAtMs)}` : 'آرشیو';
  }
  const processedAnswers = sourceAnswers.filter((answer) => answer.status === 'finalized' || answer.status === 'timeout');
  const hardAnswers = processedAnswers.filter((answer) => answer.difficulty === 'hard');
  const timedAnswers = hardAnswers.filter((answer) => typeof answer.answerMs === 'number' && answer.answerMs > 0);
  const correctAnswers = hardAnswers.filter((answer) => answer.correct);
  const fastestAnswer = timedAnswers.filter((answer) => answer.correct).sort((a, b) => Number(a.answerMs) - Number(b.answerMs))[0] ?? null;
  const averageAnswerMs = average(timedAnswers.map((answer) => Number(answer.answerMs)));
  const correctRate = hardAnswers.length ? correctAnswers.length / hardAnswers.length : 0;
  const questionRows = buildQuestionRows(hardAnswers);
  const teamRows = buildTeamRows(hardAnswers, sourceTeams);
  const difficultyRows = buildDifficultyRows(hardAnswers);
  const fastestByDifficulty = (['hard'] as const).map((difficulty) => {
    const fastest = timedAnswers
      .filter((answer) => answer.correct && answer.difficulty === difficulty)
      .sort((a, b) => Number(a.answerMs) - Number(b.answerMs))[0];
    return {
      difficulty,
      answerMs: fastest?.answerMs ?? null,
      teamName: fastest?.teamName ?? '',
      questionId: fastest?.questionId ?? '',
    };
  });

  return {
    totalAnswers: hardAnswers.length,
    sourceLabel,
    correctRateLabel: formatPercent(correctRate),
    averageAnswerLabel: formatDuration(averageAnswerMs),
    fastestAnswerLabel: fastestAnswer ? `${formatDuration(Number(fastestAnswer.answerMs))}` : '—',
    questionRows,
    teamRows,
    difficultyRows,
    fastestByDifficulty,
  };
}

function flattenAnswers(answerMap: Record<string, Record<string, AnswerRecord>>, teams: TeamPublic[]): FlatAnswer[] {
  const teamNames = new Map(teams.map((team) => [team.id, team.name]));
  const rows: FlatAnswer[] = [];
  Object.entries(answerMap).forEach(([teamId, teamAnswers]) => {
    Object.values(teamAnswers).forEach((answer) => {
      if (!answer.questionId) return;
      rows.push({
        ...answer,
        teamId,
        teamName: teamNames.get(teamId) ?? teamId.slice(0, 8),
        questionId: answer.questionId,
        difficulty: difficultyFromQuestionId(answer.questionId),
      });
    });
  });
  return rows;
}

function buildQuestionRows(answers: FlatAnswer[]) {
  const groups = groupBy(answers, (answer) => answer.questionId);
  const rows = [...groups.entries()].map(([questionId, rowsForQuestion]) => {
    const timed = rowsForQuestion.filter((answer) => typeof answer.answerMs === 'number' && answer.answerMs > 0);
    const averageMs = average(timed.map((answer) => Number(answer.answerMs)));
    return {
      questionId,
      count: rowsForQuestion.length,
      correctRate: rowsForQuestion.length ? rowsForQuestion.filter((answer) => answer.correct).length / rowsForQuestion.length : 0,
      averageMs,
      barPercent: 0,
    };
  });
  const maxAverage = Math.max(1, ...rows.map((row) => row.averageMs));
  return rows
    .map((row) => ({ ...row, barPercent: row.averageMs ? (row.averageMs / maxAverage) * 100 : 4 }))
    .sort((a, b) => b.count - a.count || b.averageMs - a.averageMs || a.questionId.localeCompare(b.questionId));
}

function buildTeamRows(answers: FlatAnswer[], teams: TeamPublic[]) {
  const groups = groupBy(answers, (answer) => answer.teamId);
  const teamScores = new Map(teams.map((team) => [team.id, team.score]));
  return [...groups.entries()]
    .map(([teamId, rows]) => {
      const timed = rows.filter((answer) => typeof answer.answerMs === 'number' && answer.answerMs > 0);
      return {
        teamId,
        teamName: rows[0]?.teamName ?? teamId.slice(0, 8),
        count: rows.length,
        correct: rows.filter((answer) => answer.correct).length,
        averageMs: average(timed.map((answer) => Number(answer.answerMs))),
        score: teamScores.get(teamId) ?? 0,
      };
    })
    .sort((a, b) => b.score - a.score || b.correct - a.correct || a.averageMs - b.averageMs);
}

function buildDifficultyRows(answers: FlatAnswer[]) {
  const rows = (['easy', 'medium', 'hard'] as const).map((difficulty) => {
    const rowsForDifficulty = answers.filter((answer) => answer.difficulty === difficulty);
    const timed = rowsForDifficulty.filter((answer) => typeof answer.answerMs === 'number' && answer.answerMs > 0);
    return {
      difficulty,
      count: rowsForDifficulty.length,
      correctRate: rowsForDifficulty.length ? rowsForDifficulty.filter((answer) => answer.correct).length / rowsForDifficulty.length : 0,
      averageMs: average(timed.map((answer) => Number(answer.answerMs))),
    };
  });
  return rows;
}

function groupBy<T>(items: T[], keyFor: (item: T) => string) {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const key = keyFor(item);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  });
  return groups;
}

function average(values: number[]) {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function onlineCountLike(states: Record<string, TeamState>) {
  const now = Date.now();
  return Object.values(states).filter((state) => now - state.lastHeartbeatMs <= 10_000).length;
}

function difficultyFromQuestionId(questionId: string): DifficultyBucket {
  if (questionId.startsWith('h')) return 'hard';
  if (questionId.startsWith('m')) return 'medium';
  return 'easy';
}

function difficultyLabel(difficulty: DifficultyBucket) {
  if (difficulty === 'hard') return 'سخت';
  if (difficulty === 'medium') return 'متوسط';
  return 'آسان';
}

function difficultyTone(difficulty: DifficultyBucket) {
  if (difficulty === 'hard') return 'bg-red-500';
  if (difficulty === 'medium') return 'bg-amber-500';
  return 'bg-emerald-500';
}

function formatDuration(value: number | null | undefined) {
  if (!value || !Number.isFinite(value)) return '—';
  return `${(value / 1000).toLocaleString('fa-IR', { maximumFractionDigits: 1 })} ثانیه`;
}

function formatPercent(value: number) {
  return `${Math.round(value * 100).toLocaleString('fa-IR')}٪`;
}

function TeamCodesPanel({
  codes,
  teams,
  busyAction,
  codeLabel,
  setCodeLabel,
  newCode,
  copiedCode,
  createTeamCode,
  codeAction,
  copyTeamCode,
}: {
  codes: TeamCode[];
  teams: TeamPublic[];
  busyAction: string;
  codeLabel: string;
  setCodeLabel: (value: string) => void;
  newCode: { code: string; teamId: string } | null;
  copiedCode: string;
  createTeamCode: () => Promise<void>;
  codeAction: (codeHash: string, nextStatus: TeamCode['status']) => Promise<void> | void;
  copyTeamCode: (code: string) => Promise<void>;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <span className="text-[10px] font-black text-brand">کدهای ورود</span>
      <h2 className="mt-1 text-lg font-black">کد تازه برای تیم</h2>
      <p className="mt-2 text-[11px] leading-6 text-slate-500">هر کد هویت همان تیم در همین مسابقه است. کدهای جدید بعد از ساخت هم همین‌جا کامل می‌مانند و قابل کپی هستند.</p>
      <div className="mt-4 flex gap-2">
        <Input value={codeLabel} onChange={(event) => setCodeLabel(event.target.value)} placeholder="مثلاً تیم آلفا" />
        <Button size="icon" variant="dark" disabled={busyAction !== ''} onClick={() => void createTeamCode()} aria-label="ساخت کد">
          <KeyRound className="size-4" />
        </Button>
      </div>
      {newCode && (
        <div className="mt-4 rounded-xl border border-brand/25 bg-brand/8 p-3">
          <span className="text-[10px] font-bold text-brand">کدی که باید به تیم بدهید</span>
          <div className="mt-1 flex items-center justify-between gap-3">
            <b className="block font-mono text-lg tracking-widest text-ink" dir="ltr">{newCode.code}</b>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2 py-1 text-[10px] font-black text-slate-600 ring-1 ring-brand/15 transition hover:bg-orange-50"
              onClick={() => void copyTeamCode(newCode.code)}
            >
              {copiedCode === newCode.code ? <CheckCircle2 className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
              {copiedCode === newCode.code ? 'کپی شد' : 'کپی'}
            </button>
          </div>
          <small className="mt-1 block text-[10px] text-slate-400" dir="ltr">{newCode.teamId}</small>
        </div>
      )}
      <div className="mt-4 max-h-72 space-y-2 overflow-auto scrollbar-thin">
        {codes.map((code) => {
          const team = teams.find((item) => item.id === code.teamId);
          const visibleCode = code.code ?? code.codePreview;
          return (
            <div key={code.id} className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <b className="block truncate text-xs">{code.label || team?.name || 'کد بی‌نام'}</b>
                  <span className="mt-1 block font-mono text-sm font-black tracking-wider text-ink" dir="ltr">{visibleCode}</span>
                  {!code.code && <span className="mt-1 block text-[10px] text-amber-600">کد قدیمی است؛ فقط پیش‌نمایش آن ذخیره شده.</span>}
                  {code.code && (
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-black text-slate-600 transition hover:bg-slate-200"
                      onClick={() => void copyTeamCode(code.code!)}
                    >
                      {copiedCode === code.code ? <CheckCircle2 className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
                      {copiedCode === code.code ? 'کپی شد' : 'کپی کد'}
                    </button>
                  )}
                </div>
                <div className="grid min-w-28 grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className={cn('min-h-8 px-2 text-[10px]', code.status === 'active' && 'bg-emerald-600 text-white hover:bg-emerald-600 hover:text-white')}
                    disabled={busyAction !== ''}
                    onClick={() => code.status !== 'active' && void codeAction(code.id, 'active')}
                  >
                    فعال
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className={cn('min-h-8 px-2 text-[10px]', code.status === 'revoked' && 'bg-red-600 text-white hover:bg-red-600 hover:text-white')}
                    disabled={busyAction !== ''}
                    onClick={() => code.status !== 'revoked' && void codeAction(code.id, 'revoked')}
                  >
                    غیرفعال
                  </Button>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400">
                <span>{team ? `استفاده‌شده توسط ${team.name}` : 'آماده برای تیم جدید'}</span>
                <span>{Number(code.redeemCount ?? 0).toLocaleString('fa-IR')} بار ورود</span>
              </div>
            </div>
          );
        })}
        {!codes.length && <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">هنوز کدی ساخته نشده است.</div>}
      </div>
    </div>
  );
}

function ArchivePanel({ archives }: { archives: ContestArchive[] }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <span className="flex items-center gap-2 text-[10px] font-black text-brand"><History className="size-4" /> آرشیو مسابقات</span>
      <h2 className="mt-1 text-lg font-black">نتایج دوره‌های قبلی</h2>
      <div className="mt-4 max-h-96 space-y-3 overflow-auto scrollbar-thin">
        {archives.map((archive, index) => (
          <details key={archive.id} className="group rounded-xl border border-slate-200 p-3 open:bg-slate-50">
            <summary className="cursor-pointer list-none">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <b className="flex items-center gap-2 truncate text-xs"><Trophy className="size-4 text-amber-500" /> مسابقه {toFaNumber(archives.length - index)}</b>
                  <span className="mt-1 block text-[10px] text-slate-400">{formatDateTime(archive.finishedAtMs)} · {archive.teamCount.toLocaleString('fa-IR')} تیم</span>
                </div>
                <div className="text-left">
                  <b className="block truncate text-xs">{archive.winnerName ?? 'بدون برنده'}</b>
                  <span className="font-mono text-[10px] text-slate-400">{archive.totalScore.toLocaleString('fa-IR')} امتیاز ترکیبی کل</span>
                </div>
              </div>
            </summary>
            <div className="mt-4 space-y-3 border-t border-slate-200 pt-3">
              <div>
                <span className="mb-2 flex items-center gap-2 text-[10px] font-black text-slate-500"><ListChecks className="size-3.5" /> رتبه‌ها</span>
                <div className="space-y-1.5">
                  {archive.teams.map((team) => (
                    <div key={team.id} className="flex items-center justify-between rounded-lg bg-white px-2 py-2 text-[11px]">
                      <span>{team.finalRank?.toLocaleString('fa-IR') ?? '—'} · {team.name}</span>
                      <span className="font-mono">{team.score.toLocaleString('fa-IR')} امتیاز ترکیبی / {team.correctCount.toLocaleString('fa-IR')} درست</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <span className="mb-2 block text-[10px] font-black text-slate-500">لاگ مدیریتی</span>
                <div className="max-h-32 space-y-1 overflow-auto rounded-lg bg-slate-950 p-2 text-[10px] text-slate-200 scrollbar-thin" dir="ltr">
                  {archive.auditLogs.map((log) => (
                    <div key={log.id}>{new Date(log.createdAtMs).toLocaleTimeString('fa-IR')} · {log.action}{log.teamId ? ` · ${log.teamId.slice(0, 8)}` : ''}</div>
                  ))}
                  {!archive.auditLogs.length && <div dir="rtl">لاگی ثبت نشده است</div>}
                </div>
              </div>
            </div>
          </details>
        ))}
        {!archives.length && <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">هنوز مسابقه‌ای آرشیو نشده است.</div>}
      </div>
    </div>
  );
}

function ScorePanel({
  teams,
  selectedTeam,
  setSelectedTeam,
  delta,
  setDelta,
  reason,
  setReason,
  busyAction,
  adjustScore,
}: {
  teams: TeamPublic[];
  selectedTeam: string;
  setSelectedTeam: (value: string) => void;
  delta: string;
  setDelta: (value: string) => void;
  reason: string;
  setReason: (value: string) => void;
  busyAction: string;
  adjustScore: () => Promise<void>;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <span className="text-[10px] font-black text-brand">داوری امتیاز</span>
      <h2 className="mt-1 text-lg font-black">تنظیم دستی امتیاز</h2>
      <div className="mt-4 space-y-3">
        <select className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs outline-none focus:border-brand" value={selectedTeam} onChange={(event) => setSelectedTeam(event.target.value)}>
          <option value="">انتخاب تیم</option>
          {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
        </select>
        <Input type="number" value={delta} onChange={(event) => setDelta(event.target.value)} placeholder="مقدار مثبت یا منفی" />
        <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="دلیل تغییر امتیاز (اجباری)" />
        <Button className="w-full" variant="dark" size="sm" disabled={!selectedTeam || !delta || reason.trim().length < 5 || busyAction !== ''} onClick={() => void adjustScore()}>
          ثبت با لاگ داوری
        </Button>
      </div>
    </div>
  );
}

function formatDateTime(value: number) {
  return new Date(value).toLocaleString('fa-IR', { dateStyle: 'short', timeStyle: 'short' });
}

function toFaNumber(value: number) {
  return value.toLocaleString('fa-IR');
}
